import React, { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";

import { ReminderView } from "../../types/reminderTypes";
import { useReminderData } from "./hooks/useReminderData";

import ViewSelector from "./components/ViewSelector/ViewSelector";
import PatientView from "./components/PatientView/PatientView";
import ArticleView from "./components/ArticleView/ArticleView";
import AggregatedView from "./components/AggregatedView/AggregatedView";
import TopFilterBar from "../../components/RFZOComponents/TopFilterBar";

import styles from "./UnifiedReminderPage.module.scss";

const UnifiedReminderPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Use refs to prevent infinite loops
  const isInitializedRef = useRef(false);
  const isSettingDefaultsRef = useRef(false);

  const {
    selectedView,
    setSelectedView,
    triggerRefresh,
    updatePatientsData,
    getCurrentLoading,
    getCurrentError,
    data,
  } = useReminderData();

  // Single initialization effect
  useEffect(() => {
    if (!isInitializedRef.current) {
      console.log("🟨 Initializing UnifiedReminderPage...");

      // Get view from URL or default to patients
      const urlView = searchParams.get("view") as ReminderView;
      const validView =
        urlView && Object.values(ReminderView).includes(urlView)
          ? urlView
          : ReminderView.PATIENTS;

      console.log(
        "🟨 Initial view from URL:",
        urlView,
        "→ valid view:",
        validView
      );

      // Set the view in the hook
      setSelectedView(validView);

      // Set default date filters if none exist
      const hasDateFilters =
        searchParams.has("createdFrom") || searchParams.has("createdTo");
      const hasOtherFilters =
        searchParams.has("filter") || searchParams.has("groups");

      if (!hasDateFilters && !hasOtherFilters) {
        console.log("🟨 Setting default date filters...");
        isSettingDefaultsRef.current = true;

        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("createdFrom", moment().format("YYYY-MM-DD"));
        newSearchParams.set(
          "createdTo",
          moment().add(30, "days").format("YYYY-MM-DD")
        );
        newSearchParams.set("view", validView);

        setSearchParams(newSearchParams, { replace: true });
      }

      isInitializedRef.current = true;
    }
  }, [searchParams, setSearchParams, setSelectedView]);

  // URL synchronization responsibilities
  // Page component manages URL - hook filter sync
  useEffect(() => {
    if (isInitializedRef.current && !isSettingDefaultsRef.current) {
      console.log("🟨 Syncing view to URL:", selectedView);
      const currentUrlView = searchParams.get("view");

      if (currentUrlView !== selectedView) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("view", selectedView);
        setSearchParams(newSearchParams, { replace: true });
      }
    }

    // Reset the flag after initial defaults
    if (isSettingDefaultsRef.current) {
      isSettingDefaultsRef.current = false;
    }
  }, [selectedView, searchParams, setSearchParams]);

  // ✅ FIX: REMOVED filter sync effect that was causing duplicate calls
  // The useReminderData hook now reads filters directly via useUnifiedFilters

  const handleViewChange = (view: ReminderView) => {
    console.log("🟨 View change requested:", view);
    if (view !== selectedView) {
      setSelectedView(view);
    }
  };

  // Safe refresh using counter (no race conditions)
  const handleRefresh = () => {
    console.log("🟨 Refresh requested");
    triggerRefresh(); // Use the hook's safe refresh mechanism
  };

  const renderActiveView = () => {
    const currentLoading = getCurrentLoading();
    const currentError = getCurrentError();

    console.log("🟨 Rendering view:", selectedView, {
      dataCount: {
        patients: data.patients?.length || 0,
        articles: data.articles?.length || 0,
        aggregated: data.aggregated?.length || 0,
      },
      loading: currentLoading,
      error: currentError,
    });

    switch (selectedView) {
      case ReminderView.PATIENTS:
        return (
          <PatientView
            data={data.patients}
            loading={currentLoading}
            error={currentError}
            onRefresh={handleRefresh}
            onPatientsUpdate={updatePatientsData}
          />
        );
      case ReminderView.ARTICLES:
        return (
          <ArticleView
            data={data.articles}
            loading={currentLoading}
            error={currentError}
            onRefresh={handleRefresh}
          />
        );
      case ReminderView.AGGREGATED:
        return (
          <AggregatedView
            data={data.aggregated}
            loading={currentLoading}
            error={currentError}
            onRefresh={handleRefresh}
          />
        );
      default:
        return <div>Nepoznata stranica</div>;
    }
  };

  return (
    <div className={styles.unifiedReminderPage}>
      <div className={styles.pageHeader}>
        <h1>Podsetnici</h1>
        <ViewSelector
          selectedView={selectedView}
          onViewChange={handleViewChange}
          loading={getCurrentLoading()}
        />
      </div>

      <TopFilterBar selectedView={selectedView} />

      <div className={styles.contentLayout}>
        <div className={styles.mainColumn}>{renderActiveView()}</div>
      </div>
    </div>
  );
};

export default UnifiedReminderPage;
